/**
 * Hook pour gérer les permissions utilisateur côté frontend
 * Permet de vérifier si l'utilisateur a accès à certains modules ou fonctionnalités
 */

import { useMemo } from 'react';

/**
 * Mapping des modules vers les permissions requises
 * Doit être synchronisé avec le backend (middleware/permissions.js)
 */
const MODULE_PERMISSIONS = {
  dashboard: [], // Accessible à tous les utilisateurs connectés
  users: ['users.manage'],
  groups: ['planning.create', 'planning.edit'], // Inclut gestion des membres
  clients: ['planning.create', 'planning.edit'],
  locations: ['planning.create', 'planning.edit'], // Lieux de mise
  hr: ['availabilities.manage', 'users.manage'],
  worksheets: ['availabilities.manage', 'users.manage', 'worksheets.view_own'], // Feuilles journalières (RH + Chefs d'équipe)
  'work-sheets': ['work-sheets.access', 'work-sheets.create'], // Fiches de chantier PDF
  planning: ['planning.view'], // Planning général (lecture seule pour CE, éditable pour planificateurs)
  'my-planning': ['planning.view_own'], // Mon planning personnel (Employés, CE)
  holidays: ['holidays.manage'], // Jours fériés (planificateurs + admin)
  permissions: ['all'], // Réservé aux admins uniquement
  'email-templates': ['all'], // Templates email (admin uniquement)
  'user-import': ['users.import', 'all'], // Import utilisateurs (admin + planificateur)
  analytics: ['all'], // Analytics dashboard (admin uniquement)
  'ocr-test': ['all'], // Test OCR (admin uniquement)
  'distance-api': ['all'], // Comparaison APIs distance (admin uniquement)
  // Congés - contrôlé par permissions
  'leave-requests': ['leaves.access', 'leaves.create'], // Demandes de congés
  'my-leaves': ['leaves.access', 'leaves.create'], // Menu Mes congés
  'leaves-validation': ['leaves.validate'], // Validation congés (RH)
  // Frais - contrôlé par permissions
  expenses: ['expenses.access', 'expenses.create'], // Module frais
  'my-expenses': ['expenses.access', 'expenses.create'], // Menu Mes frais
  'expenses-validation': ['expenses.validate'], // Validation frais (Comptabilité)
  // Feedback
  feedback: ['feedback.view'] // Retours de tests
};

/**
 * Vérifie si une permission est présente dans la liste des permissions utilisateur
 * @param {string[]} userPermissions - Liste des permissions de l'utilisateur
 * @param {string} permission - Permission à vérifier
 * @returns {boolean}
 */
const checkPermission = (userPermissions, permission) => {
  if (!userPermissions || userPermissions.length === 0) return false;

  // Admin a toutes les permissions
  if (userPermissions.includes('all')) return true;

  // Permission exacte
  if (userPermissions.includes(permission)) return true;

  // Vérifier wildcard (ex: 'planning.*' autorise 'planning.view')
  const permissionParts = permission.split('.');
  if (permissionParts.length > 1) {
    const wildcardPermission = `${permissionParts[0]}.*`;
    if (userPermissions.includes(wildcardPermission)) return true;
  }

  return false;
};

/**
 * Hook principal pour gérer les permissions
 * @param {Object} user - Objet utilisateur contenant role et permissions
 * @returns {Object} Fonctions et données de permissions
 */
export const usePermissions = (user) => {
  const permissions = useMemo(() => {
    if (!user) return [];

    // Récupérer les permissions depuis l'utilisateur ou le token décodé
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions;
    }

    // Fallback: essayer de décoder le token depuis localStorage
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Décodage avec support UTF-8 (pour les noms avec accents)
        const base64 = token.split('.')[1];
        const binaryString = atob(base64);
        const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
        const payload = JSON.parse(new TextDecoder().decode(bytes));
        return payload.permissions || [];
      }
    } catch (e) {
      console.warn('Impossible de décoder le token pour les permissions');
    }

    return [];
  }, [user]);

  const role = useMemo(() => {
    return user?.role || 'user';
  }, [user]);

  const isAdmin = useMemo(() => {
    return permissions.includes('all') || role === 'Admin';
  }, [permissions, role]);

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   * @param {string} permission - Permission à vérifier
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    return checkPermission(permissions, permission);
  };

  /**
   * Vérifie si l'utilisateur a au moins une des permissions listées
   * @param {string[]} requiredPermissions - Liste des permissions à vérifier
   * @returns {boolean}
   */
  const hasAnyPermission = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.some(perm => checkPermission(permissions, perm));
  };

  /**
   * Vérifie si l'utilisateur a toutes les permissions listées
   * @param {string[]} requiredPermissions - Liste des permissions à vérifier
   * @returns {boolean}
   */
  const hasAllPermissions = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.every(perm => checkPermission(permissions, perm));
  };

  /**
   * Vérifie si l'utilisateur a accès à un module spécifique
   * @param {string} moduleId - ID du module (ex: 'users', 'planning', 'hr')
   * @returns {boolean}
   */
  const canAccessModule = (moduleId) => {
    const requiredPermissions = MODULE_PERMISSIONS[moduleId];

    // Module sans restriction
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    // Admin a accès à tout
    if (isAdmin) {
      return true;
    }

    // Vérifier si l'utilisateur a au moins une des permissions requises
    return hasAnyPermission(requiredPermissions);
  };

  /**
   * Filtre une liste de modules pour ne garder que ceux accessibles
   * @param {Object[]} modules - Liste des modules avec leur id
   * @returns {Object[]} Modules filtrés
   */
  const filterAccessibleModules = (modules) => {
    return modules.filter(module => canAccessModule(module.id));
  };

  /**
   * Retourne les modules accessibles avec leur état
   */
  const accessibleModules = useMemo(() => {
    const result = {};
    for (const moduleId of Object.keys(MODULE_PERMISSIONS)) {
      result[moduleId] = canAccessModule(moduleId);
    }
    return result;
  }, [permissions, isAdmin]);

  return {
    // Données
    permissions,
    role,
    isAdmin,
    accessibleModules,

    // Fonctions de vérification
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessModule,
    filterAccessibleModules,

    // Constantes
    MODULE_PERMISSIONS
  };
};

export { MODULE_PERMISSIONS };
export default usePermissions;
